import React from "react"

import {
	Box,
	Pagination,
	Typography,
} from "@mui/material"

import { ErrorResponse, Loading, RestResponse, } from "@sinossi/mates-react-library"
import { mdiInformationOutline } from "@mdi/js"
import Icon from "@mdi/react"
import { Trans } from "@lingui/react"

import { AssetAdvancedSearch } from "components/asset/advanced-search"
import { CreateAssetModal } from "components/asset/asset-details"
import { PageHeader, RoutesData, } from "components/commons"
import { SbSnackbar } from "components/commons/sb-snackbar"
import { MatesComponent, MatesComponentState, } from "components/mates-component"
import {
	AccountType,
	AssetFilter,
	AssetWithImage,
	CreateAssetRequest,
	CreateAssetResponse,
	ListLandlordsProjection,
	OrderType,
	PaginatedResponse,
	SearchFilter,
	SessionResponse
} from "models"
import { AssetService } from "services/asset.service"
import withUseRouter, { UseRouterParams } from "services/router"
import { AccountServices } from "../../../services/account.services"
import DashboardAssetListItem from "components/dashboard/dashboard-assets-list/dashboard-asset-list-item"

interface PropertyManagementProps extends UseRouterParams {
	sessionData: SessionResponse
	isInLandlordDashboard?: boolean
}
interface PropertyManagementState extends MatesComponentState {
	openModalCreate: boolean,
	request: AssetFilter | null,
	assetData: PaginatedResponse<AssetWithImage> | null,
	landlordList: PaginatedResponse<ListLandlordsProjection> | null,
	likes: string[]
	snackbarData: {
		openNotification: boolean,
		severity: 'error' | 'info' | 'success' | 'warning',
		notificationMessage: string
	}
	pagination: SearchFilter
}
class AssetList extends React.Component<PropertyManagementProps, PropertyManagementState> {
	private readonly matesComponent: MatesComponent<PropertyManagementProps, PropertyManagementState> = new MatesComponent(this)

	constructor(props: PropertyManagementProps) {
		super(props);
		this.state = {
			...this.matesComponent.setupState(),
			openModalCreate: false,
			loading: true,
			request: {
				pageSize: 25,
				pageId: 0,
				orderType: OrderType.DESCENDING,
				orderBy: "creationDate"
			},
			snackbarData: {
				openNotification: false,
				notificationMessage: "",
				severity: "info"
			},
			pagination: {
				pageSize: 25,
				pageId: 0,
				orderType: OrderType.DESCENDING,
				orderBy: "creationDate"
			},
			assetData: null,
			landlordList: null,
			likes: [],
		};
	}

	componentDidMount(): void {
		this.initializeRequest()
	}

	private initializeRequest = () => {
		this.filterAssets()
	}

	private handleTableCallback = (data: Pick<SearchFilter, "pageSize" | "pageId" | "orderBy" | "orderType">) => {
		this.setState(() => ({
			loading: true,
			pagination: {
				pageSize: data.pageSize,
				pageId: data.pageId,
				orderBy: data.orderBy,
				orderType: data.orderType,
			},
			request: {
				...this.state.request,
				pageSize: data.pageSize,
				pageId: data.pageId,
				orderBy: data.orderBy,
				orderType: data.orderType,
			},
		}), this.filterAssets)
	}

	private openModalCreate = (): void => {
		this.setState(() => ({ openModalCreate: true }))
	}

	private closeModalCreate = (): void => {
		this.setState(() => ({ openModalCreate: false }))
	}

	private filterAssets = (): void => {
		AssetService.filterAssets(
			this.state.request,
			this.updateAssetData,
			this.matesComponent.onException
		)
	}

	private getLandlord = () => {
		AssetService.getLandlord(this.getLandlordCallback, this.getLandlordCallbackError)
	}

	private getLandlordCallback = (response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>): void => {

		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({ loading: false, landlordList: response.data }))
	}

	private getLandlordCallbackError = (response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>): void => {
		this.setState(() => ({ landlordList: null, loading: false }))
		this.matesComponent.onError(response)
	}


	private updateAssetData = (response: RestResponse<PaginatedResponse<AssetWithImage>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(
			() => ({ assetData: response.data, }),
			() => AccountServices.getAssetLikes(this.getAssetLikesCallback, this.matesComponent.onException)
		)
	}

	private getAssetLikesCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({
			likes: response.data
		}), this.getLandlord)
	}

	private handleSearchbarCallback = (data: AssetFilter): void => {
		this.setState(() => ({
			loading: true,
			request: {
				...data,
				pageId: 0,
				pageSize: this.state.pagination.pageSize,
				orderBy: this.state.pagination.orderBy,
				orderType: this.state.pagination.orderType
			},
			pagination: {
				...this.state.pagination,
				pageId: 0,
			},
		}), this.filterAssets)
	}

	private createAsset = (values: any) => {
		let request: CreateAssetRequest = {
			address: values.address,
			landlordId: values.owner,
		}
		this.closeModalCreate()

		this.setState(() => ({ loading: true }))
		AssetService.createAsset(request, this.createAssetCallback, this.matesComponent.onError)
	}

	private createAssetCallback = (response: RestResponse<CreateAssetResponse, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			loading: false,
		}))

		const { navigate } = this.props
		navigate(`/asset/${response.data?.id}`)
	}

	private handleCloseNotification = () => {
		this.setState(() => ({
			snackbarData: {
				...this.state.snackbarData,
				openNotification: false
			}
		}))
	}

	private handlePagination = (_: any, value: number) => {
		if (value) {
			this.setState(() => ({
				request: {
					...this.state.request,
					pageId: value - 1,
					pageSize: this.state.pagination.pageSize,
					orderType: this.state.pagination.orderType,
					orderBy: this.state.pagination.orderBy
				},
				loading: true,
				pagination: {
					...this.state.pagination,
					pageId: value - 1,
					pageSize: this.state.pagination.pageSize,
				}
			}), this.filterAssets)
		}
	}

	render() {

		const { isInLandlordDashboard } = this.props;

		return (
			<Box sx={{ width: '100%', pt: 2, position: "relative" }}>
				<Loading show={this.state.loading} />
				{!isInLandlordDashboard && <PageHeader
					title={RoutesData.ASSET_LIST.title}
					paths={[RoutesData.DASHBOARD, RoutesData.ASSET_LIST]}
					addButtonModal={"Aggiungi Immobile"}
					onClickButtonModal={this.openModalCreate}
				/>}
				{!isInLandlordDashboard && <AssetAdvancedSearch
					isInLandlordDashboard
					handleCallback={this.handleSearchbarCallback} />}
				<Box
					sx={{
						mt: 2,
						display: "flex",
						flexDirection: "column",
						gap: 2
					}}
				>
					<Pagination
						showFirstButton
						showLastButton
						page={this.state.assetData ? this.state.assetData.pagination.currentPage + 1 : 1}
						count={this.state.assetData ? this.state.assetData.pagination.totalPages : 1}
						onChange={this.handlePagination}
						siblingCount={2}
						sx={{
							mb: 4,
							display: "flex",
							width: "100%",
							justifyContent: "right",
						}}
					/>
						{
							this.state.assetData?.items.map((asset) => (
								<DashboardAssetListItem
									key={asset.asset.id}
									asset={asset}
									likes={this.state.likes}
								/>
							))
						}
					{
						!this.state.loading && (!this.state.assetData?.items || this.state.assetData.items.length === 0) &&
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								backgroundColor: "#f9fafb",
								borderRadius: "10px",
								flex: 1,
								mb: 2,
								px: 2,
								py: 3,
								width: "100%",
								minHeight: "130px",
								gap: 1
							}}
						>
							<Icon 
								path={mdiInformationOutline} 
								size={2} 
								color="#9ca3af"
							/>
							<Typography 
								variant="body1" 
								color="text.secondary"
								sx={{ textAlign: "center" }}
							>
								<Trans id={"asset-list.empty-list"} message={"Nessun immobile presente"} />
							</Typography>
						</Box>
					}
				</Box>

				{this.state.openModalCreate &&
					<CreateAssetModal
						closeModal={this.closeModalCreate}
						isCreatingAccountLandlord={this.props.sessionData.accountType === AccountType.LANDLORD}
						submitForm={this.createAsset}
						landlordList={this.state.landlordList?.items ?? []}
					/>
				}
				<SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />
			</Box >
		)
	}
}

export default withUseRouter(AssetList)
